import {view} from '@sail/ui';
import {FormattedMessage, defineMessages} from 'react-intl';
import {useEffect} from 'react';
import {useAnalytics} from '@stripe-internal/inline-data';
import {SafeAnchor} from '@stripe-internal/safe-links';
import {Audience, Payout} from './MerchantPayoutStatus';
import {FailedNextStepsMessage} from './FailedNextStepsMessage';
import {UpdatePayoutDetailsInlineButton} from './StatusUpdatePayoutDetails';
import {formatUtcDate} from './date';
import {TraceIdCopyButton} from './TraceIdCopyButton';

const messages = defineMessages({
  paidInstructions: {
    id: 'mse_inline_data.merchant_payout_status.paid_instructions',
    description: 'Message displaying paid instruction.',
    defaultMessage: 'Money was sent from Stripe to your {sent_to}. ',
  },
  inTransitInstructions: {
    id: 'mse_inline_data.merchant_payout_status.in_transit_instructions',
    description: 'Message displaying in transit instruction.',
    defaultMessage: 'Money is on the way from Stripe to your {sent_to}. ',
  },
  traceIdAddendum: {
    id: 'mse_inline_data.merchant_payout_status.trace_id_addendum',
    description: 'Message displaying instruction details about the trace ID.',
    defaultMessage:
      'Provide your bank the <b>Trace ID {trace_id}</b>, which will help them locate your payout.',
  },
  expectedDateAddendum: {
    id: 'mse_inline_data.merchant_payout_status.expected_date_addendum',
    description:
      'Message addendum to instructions with details of the expected deposit date.',
    defaultMessage:
      'If you haven’t received this payout by {expected_deposit_date}, please reach out to your bank. ',
  },
  firstPayoutAddendum: {
    id: 'mse_inline_data.merchant_payout_status.first_payout_addendum',
    description:
      'Message addendum to instructions with details for the first payout on an account.',
    defaultMessage:
      '<b>Your first payout with Stripe will have a delay of about 7 to 14 days.</b> This delay is necessary for risk mitigation and cannot be waived under any circumstances. ',
  },
  firstPayoutAfterPreconditionsMetAddendum: {
    id: 'mse_inline_data.merchant_payout_status.first_payout_after_preconditions_met_addendum',
    description:
      'Message addendum to instructions with details for the first payout on an account when no payout exists yet due to missing preconditions.',
    defaultMessage:
      'Note that your first payout with Stripe will have a delay of <b>about 7 to 14 days</b> after you add your bank account and take your first successful payment on Stripe.',
  },
  firstPayoutDelayAddendum: {
    id: 'mse_inline_data.merchant_payout_status.first_payout_delay_addendum',
    description:
      'Message addendum to instructions on why the first payout delay cannot be waived.',
    defaultMessage:
      'This delay is necessary for risk mitigation and cannot be waived under any circumstances.',
  },
  updateBankAccountInstructions: {
    id: 'mse_inline_data.merchant_payout_status.update_bank_account_instructions',
    description: 'Message displaying update bank account instruction.',
    defaultMessage:
      '{error} Review and update these details to resend this payout and receive future payouts.',
  },
  genericFailureInstructions: {
    id: 'mse_inline_data.merchant_payout_status.generic_failure_instructions',
    description: 'Message displaying update failure instruction.',
    defaultMessage:
      'Your {sent_to} payout couldn’t be completed. To find out possible reasons why, ',
  },
  payoutFailuresLinkCaption: {
    id: 'mse_inline_data.merchant_payout_status.payout_failures_link_caption',
    description:
      'Message displaying link to support article for learning more about payment failures',
    defaultMessage: 'learn more about payout failures.',
  },
  canceledInstructions: {
    id: 'mse_inline_data.merchant_payout_status.canceled_instructions',
    description: 'Message displaying instructions for a canceled payout.',
    defaultMessage:
      'Your payout was canceled. To find out possible reasons why, ',
  },
  noPayoutFound: {
    id: 'mse_inline_data.merchant_payout_status.no_payout_found',
    description: 'Message displayed when there is no payout found.',
    defaultMessage:
      'There are no payouts available for your account at this time.',
  },
  noPayoutExpressContactPlatform: {
    id: 'mse_inline_data.merchant_payout_status.no_payout_express_contact_platform',
    description:
      'Message displayed when there is no payout found and the express user is instructed to contact their platform.',
    defaultMessage:
      'If you need additional help, reach out directly to your platform{platform_name}.',
  },
  showManualPayout: {
    id: 'mse_inline_data.merchant_payout_status.has_manual_payout',
    description: 'Message displayed when there is a manual payout.',
    defaultMessage:
      '{platform_name} manages your payouts, including your payout schedule and creation. If you have questions regarding your payouts, contact {platform_name} for help.',
  },
});

const payoutFailuresLink = (audience?: Audience) =>
  audience === 'express'
    ? 'https://support.stripe.com/express/questions/understanding-the-status-of-your-payout?ref=universal-help-express-payout-flow'
    : 'https://docs.stripe.com/payouts?ref=universal-help-merchant-payout-flow#payout-failures';

export const payoutDetailsNeedUpdate = (payout: Payout) =>
  payout.status === 'failed' &&
  [
    // BankAccount
    'account_closed',
    'declined',
    'invalid_account_number',
    'incorrect_account_holder_name',
    'incorrect_account_holder_details',
    'incorrect_account_holder_address',
    'incorrect_account_holder_tax_id',
    'incorrect_account_type',
    'invalid_account_number',
    'invalid_account_number_length',
    'no_account',
    'unsupported_card',

    // Card
    'exceeds_amount_limit',
    'exceeds_count_limit',
    'expired_card',
    'invalid_card',
    'invalid_issuer',
    'routing_destination_unknown',
    'unsupported_card',
    // 'declined' (duplicate of BankAccount)
  ].includes(payout.failure_code || '');

export const buildFormattedSentTo = (payout?: Payout) =>
  ({
    bank_account: 'bank account',
    card: 'card',
    stripe_account: 'Stripe account',
    blockchain_address: 'blockchain address',
    financial_account_balance: 'Financial account balance',
    '': 'bank',
  }[payout?.destination?.type || '']);

// The text "probably incorrect" is provided by the Stripe API for specific types of bank account
// failures (e.g. "The bank account details are probably incorrect"), but is not ideal user messaging.
// Because some users hardcode the text returned from the API in their Stripe integration, we
// cannot tweak the text in the API itself, so instead we manually replace the message.
const formatFailureMessage = (message: string) =>
  message.replace('probably incorrect', 'incorrect');

export const StatusDescription = ({
  payout,
  isFirstPayout,
  hasUpdatePayoutPermissions,
  merchantToken,
  audience,
  showManualPayoutMessage,
  platformName,
  universalHelpNewDesign,
}: {
  payout: Payout | undefined;
  isFirstPayout: boolean | undefined;
  hasUpdatePayoutPermissions: boolean | undefined;
  merchantToken?: string;
  audience?: Audience;
  showManualPayoutMessage: boolean | undefined;
  platformName: string | undefined;
  universalHelpNewDesign: boolean | undefined;
}) => {
  const analytics = useAnalytics();
  useEffect(() => {
    analytics.viewed('uh_merchant_payout_status', {
      uh_merchant_payout_status: payout?.status,
      uh_merchant_payout_status_destination_type: payout?.destination?.type,
      uh_merchant_payout_status_failure_code: payout?.failure_code,
      uh_merchant_payout_status_has_trace_id: payout?.trace_id
        ? 'true'
        : 'false',
      uh_merchant_payout_status_first_payout: isFirstPayout,
      uh_merchant_payout_status_has_update_payout_permissions:
        hasUpdatePayoutPermissions,
      uh_merchant_payout_status_show_manual_payout_message:
        showManualPayoutMessage,
    });
  }, [
    analytics,
    hasUpdatePayoutPermissions,
    isFirstPayout,
    payout?.destination?.type,
    payout?.failure_code,
    payout?.status,
    payout?.trace_id,
    showManualPayoutMessage,
  ]);

  const formattedSentTo = buildFormattedSentTo(payout);

  const localizedExpectedDate = formatUtcDate(payout?.date);

  const expectedDateAddendum = localizedExpectedDate ? (
    <FormattedMessage
      {...messages.expectedDateAddendum}
      values={{
        expected_deposit_date: <b>{localizedExpectedDate}</b>,
      }}
    />
  ) : null;
  const traceIdCopyButton = payout?.trace_id ? (
    <TraceIdCopyButton payout={payout} />
  ) : (
    <></>
  );

  const traceIdAddendum = payout?.trace_id ? (
    <view.div data-testid="payout-trace-id" css={{font: 'label.small'}}>
      <FormattedMessage
        {...messages.traceIdAddendum}
        values={{
          trace_id: <code>{payout.trace_id}</code>,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
    </view.div>
  ) : null;

  const firstPayoutAddendum = (
    <view.div
      data-testid="payout-pending-first-payout"
      css={{font: 'label.small'}}
    >
      <FormattedMessage
        {...messages.firstPayoutAddendum}
        values={{
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
    </view.div>
  );

  const showManualPayout = (
    <view.div
      data-testid="payout-manual"
      css={{font: 'label.small', stack: 'y', gap: 'xsmall'}}
    >
      <view.div css={{font: 'label.medium.emphasized'}}>
        Manual Payouts
      </view.div>
      <FormattedMessage
        {...messages.showManualPayout}
        values={{
          platform_name: platformName,
        }}
      />
    </view.div>
  );

  const noPayoutExpressContactPlatform = (
    <view.div data-testid="no-payout-4" css={{font: 'label.small'}}>
      <FormattedMessage
        {...messages.noPayoutExpressContactPlatform}
        values={{
          platform_name: platformName ? ` ${platformName}` : '',
        }}
      />
    </view.div>
  );

  // Opus::TransferPayout::Model::Transfer.smart_status
  switch (payout?.status) {
    case 'pending':
    case 'in_transit':
      return (
        <>
          <view.div data-testid="payout-pending" css={{font: 'label.small'}}>
            <FormattedMessage
              {...messages.inTransitInstructions}
              values={{
                sent_to: formattedSentTo,
              }}
            />
            {expectedDateAddendum}
          </view.div>
          {payout.trace_id ? traceIdAddendum : null}
          {isFirstPayout ? firstPayoutAddendum : null}
          {payout.trace_id ? traceIdCopyButton : null}
          {showManualPayoutMessage ? showManualPayout : null}
        </>
      );
    case 'paid':
      return (
        <>
          <view.div data-testid="payout-paid" css={{font: 'label.small'}}>
            <FormattedMessage
              {...messages.paidInstructions}
              values={{
                sent_to: formattedSentTo,
              }}
            />
            {expectedDateAddendum}
          </view.div>
          {payout.trace_id ? traceIdAddendum : null}
          {isFirstPayout ? firstPayoutAddendum : null}
          {payout.trace_id ? traceIdCopyButton : null}
          {showManualPayoutMessage && audience === 'express'
            ? showManualPayout
            : null}
        </>
      );
    case 'failed':
      if (payout.failure_message) {
        return (
          <>
            <view.div
              data-testid={`payout-failed-${payout.failure_code}`}
              css={{font: 'label.small'}}
            >
              {formatFailureMessage(payout.failure_message)}
            </view.div>
            <FailedNextStepsMessage payout={payout} />
            <UpdatePayoutDetailsInlineButton
              updatePayoutPermissions={hasUpdatePayoutPermissions}
              payout={payout}
              merchantToken={merchantToken}
              audience={audience}
              universalHelpNewDesign={universalHelpNewDesign}
            />
          </>
        );
      } else {
        return (
          <>
            <view.div data-testid="payout-failed" css={{font: 'label.small'}}>
              <FormattedMessage
                {...messages.genericFailureInstructions}
                values={{
                  sent_to: formattedSentTo,
                }}
              />
              <SafeAnchor
                href={payoutFailuresLink(audience)}
                onClick={() => {
                  analytics.action(
                    'uh_merchant_payout_status_payout_failures_link_clicked_failed',
                  );
                }}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage {...messages.payoutFailuresLinkCaption} />
              </SafeAnchor>
            </view.div>
            <FailedNextStepsMessage payout={payout} />
            <UpdatePayoutDetailsInlineButton
              payout={payout}
              updatePayoutPermissions={hasUpdatePayoutPermissions}
              merchantToken={merchantToken}
              audience={audience}
              universalHelpNewDesign={universalHelpNewDesign}
            />
          </>
        );
      }

    case 'canceled':
      return (
        <>
          <view.div data-testid="payout-canceled" css={{font: 'label.small'}}>
            <FormattedMessage
              {...messages.canceledInstructions}
              values={{
                sent_to: formattedSentTo,
              }}
            />
            <SafeAnchor
              href={payoutFailuresLink(audience)}
              onClick={() => {
                analytics.action(
                  'uh_merchant_payout_status_payout_failures_link_clicked_cancelled',
                );
              }}
              target="_blank"
              rel="noreferrer"
            >
              <FormattedMessage {...messages.payoutFailuresLinkCaption} />
            </SafeAnchor>
          </view.div>
        </>
      );
    // No payout
    default:
      return (
        <>
          <view.div data-testid="no-payout" css={{font: 'label.small'}}>
            <FormattedMessage {...messages.noPayoutFound} />
          </view.div>
          <view.div data-testid="no-payout-2" css={{font: 'label.small'}}>
            <FormattedMessage
              {...messages.firstPayoutAfterPreconditionsMetAddendum}
              values={{
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </view.div>
          <view.div data-testid="no-payout-3" css={{font: 'label.small'}}>
            <FormattedMessage
              {...messages.firstPayoutDelayAddendum}
              values={{
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </view.div>
          {platformName && audience === 'express'
            ? noPayoutExpressContactPlatform
            : null}
        </>
      );
  }
};
